/*
 * thin I/O layer for the Teacher Payments tabs (goserver /v2 routes, JWT + requirePaymentGrant on
 * every route).
 */
import { get_api, post_api } from '../../../redux/api_funcs';
import { get_api_url } from '../../../utils/urls';
import { envelope_data, extract_csv_payload } from './format';
import { build_query_string } from './overview/params';
import { disposition_file_name } from './shared/file_name';
import {
  csv_params as report_csv_params,
  data_params,
  issues_csv_params,
  status_params
} from './reports/requests';
import { summary_params } from './needs_attention/params';
import {
  run_params as month_run_params,
  runs_params as month_runs_params
} from './needs_attention/month_run/params';
import {
  list_params,
  sessions_lookup_params,
  teachers_lookup_params
} from './additional_payments/params';
import {
  file_params,
  run_params,
  runs_params,
  to_form_data,
  validate_fields
} from './additional_payments/upload/requests';
import {
  csv_params as close_versions_csv_params,
  list_params as close_versions_list_params,
  summary_params as close_versions_summary_params,
  teacher_month_params
} from './close_versions/requests';
import { preview_params, text_params } from './email_logs/requests';
import {
  awaiting_params,
  ifsc_params,
  pending_params,
  view_params
} from './payment_details/requests';
import { outbox_params } from './payment_emails/requests';
import { list_params as invoice_list_params, pdf_params } from './invoices/requests';
import {
  apply_body,
  claims_params,
  deleted_params,
  deleted_teacher_params,
  eligibility_params,
  file_csv_params,
  files_params,
  held_params,
  holidays_params,
  preview_params as response_preview_params,
  teacher_month_params as payout_teacher_month_params,
  upload_fields
} from './payouts/requests';
import { with_json_error } from './shared/blob_error';

const GROUP = '/v2/cms_user/teacher_payments';

const group_url = (path, params = {}) => {
  const query = build_query_string(params);
  return get_api_url(`${GROUP}${path}${query ? `?${query}` : ''}`);
};

const with_query = (path, params = {}) => group_url(`/overview${path}`, params);

export const fetch_months = () => get_api(with_query('/months'), true).then(envelope_data);

export const fetch_month_summary = (pay_month) =>
  get_api(with_query('/month_summary', { pay_month }), true).then(envelope_data);

// params: overview/params.js l1_params
export const fetch_l1 = (params) => get_api(with_query('/l1', params), true).then(envelope_data);

export const fetch_l2 = (params) => get_api(with_query('/l2', params), true).then(envelope_data);

// params: overview/params.js drilldown_params
export const fetch_drilldown = (params) =>
  get_api(with_query('/drilldown', params), true).then(envelope_data);

// Server-generated text/csv (tie-out export); the caller normalises via extract_csv_payload.
export const fetch_csv = (params) =>
  get_api(with_query('/csv', params), true).then((res) => res.data);

/*
 * Needs attention (issue log) + month close/reopen. Same group, same envelope; the writes are
 * POSTs whose bodies come from needs_attention/params.js and whose 4xx carry a stable error_code.
 */
export const fetch_needs_attention_summary = (pay_month) =>
  get_api(group_url('/needs_attention/summary', summary_params(pay_month)), true).then(
    envelope_data
  );

// params: needs_attention/params.js issues_params
export const fetch_needs_attention_issues = (params) =>
  get_api(group_url('/needs_attention/issues', params), true).then(envelope_data);

// params: needs_attention/params.js readiness_params
export const fetch_needs_attention_readiness = (params) =>
  get_api(group_url('/needs_attention/readiness', params), true).then(envelope_data);

// params: needs_attention/params.js daily_params
export const fetch_needs_attention_daily = (params) =>
  get_api(group_url('/needs_attention/daily', params), true).then(envelope_data);

export const post_issue_action = (payload) =>
  post_api(group_url('/needs_attention/issue_action'), payload, true).then(envelope_data);

// The month-close switch: every page header reads it; the POST body comes from close_switch/adapters.js.
export const fetch_close_switch = () =>
  get_api(group_url('/close_switch'), true).then(envelope_data);

export const post_close_switch = (payload) =>
  post_api(group_url('/close_switch'), payload, true).then(envelope_data);

// a request that walks many rows, each waiting on its lock (penalties bulk, the upload apply), must
// not hit the 30s default and read as a dropped connection.
export const MONTH_BATCH_TIMEOUT_MS = 120000;

/* Same group and grant gate; query params and bodies come from penalties/requests.js. */
const penalties_url = (path, params = {}) => group_url(`/penalties${path}`, params);

export const fetch_penalties_list = (params) =>
  get_api(penalties_url('/list', params), true).then(envelope_data);

export const fetch_penalties_summary = (params) =>
  get_api(penalties_url('/summary', params), true).then(envelope_data);

export const fetch_penalties_session = (params) =>
  get_api(penalties_url('/session', params), true).then(envelope_data);

export const fetch_penalties_free_pass = (params) =>
  get_api(penalties_url('/free_pass', params), true).then(envelope_data);

export const post_penalty_correct = (payload) =>
  post_api(penalties_url('/correct'), payload, true).then(envelope_data);

export const post_penalty_waive = (payload) =>
  post_api(penalties_url('/waive'), payload, true).then(envelope_data);

export const post_penalty_revert = (payload) =>
  post_api(penalties_url('/revert'), payload, true).then(envelope_data);

export const post_penalty_remove = (payload) =>
  post_api(penalties_url('/remove'), payload, true).then(envelope_data);

export const post_penalty_add = (payload) =>
  post_api(penalties_url('/add'), payload, true).then(envelope_data);

export const post_penalty_free_pass = (payload) =>
  post_api(penalties_url('/free_pass'), payload, true).then(envelope_data);

// remove or give back a teacher-month's own Free Pass Day (reason required).
export const post_penalty_month_pass = (payload) =>
  post_api(penalties_url('/free_pass/month_pass'), payload, true).then(envelope_data);

export const post_penalties_bulk = (payload) =>
  post_api(penalties_url('/bulk'), payload, true, 'none', MONTH_BATCH_TIMEOUT_MS).then(
    envelope_data
  );

/*
 * Additional payments (manual entries). Same JWT group and the same requirePaymentGrant on every
 * route, reads included; params come from additional_payments/params.js.
 */
const additional_url = (path, params = {}) => group_url(`/additional${path}`, params);

export const fetch_additional_categories = () =>
  get_api(additional_url('/categories'), true).then(envelope_data);

export const fetch_additional_list = (filters, limit, offset) =>
  get_api(additional_url('/list', list_params(filters, limit, offset)), true).then(envelope_data);

export const fetch_additional_teachers = (q) =>
  get_api(additional_url('/teachers_lookup', teachers_lookup_params(q)), true).then(envelope_data);

export const fetch_additional_sessions = (params) =>
  get_api(additional_url('/sessions_lookup', sessions_lookup_params(params)), true).then(
    envelope_data
  );

export const create_additional = (payload) =>
  post_api(additional_url('/create'), payload, true).then(envelope_data);

export const update_additional = (payload) =>
  post_api(additional_url('/update'), payload, true).then(envelope_data);

export const remove_additional = (payload) =>
  post_api(additional_url('/remove'), payload, true).then(envelope_data);

/*
 * Reports and the to-be-reviewed CSV; the audit trail and the Rules tab keep their calls in
 * audit/api.js and rules_engine/api.js. Every month is 'YYYY-MM' except /recalc's 'YYYY-MM-01'.
 */

// A server text/csv plus the file name its Content-Disposition carries, when the browser may read it
// ('' otherwise: the header is not CORS-exposed, so the caller names the file the same way the server does).
const csv_download = (res) => ({
  csv: extract_csv_payload(res && res.data),
  file_name: disposition_file_name(res && res.headers)
});

export const fetch_report_status = (pay_month) =>
  get_api(group_url('/reports/status', status_params(pay_month)), true).then(envelope_data);

export const fetch_report_data = (report, pay_month, preview) =>
  get_api(group_url('/reports/data', data_params(report, pay_month, preview)), true).then(
    envelope_data
  );

export const fetch_report_csv = (report, pay_month) =>
  get_api(group_url('/reports/csv', report_csv_params(report, pay_month)), true).then(csv_download);

// filters: the issue list's own (pay_month, tier, issue_code, teacher_uuid, status, sort, dir).
export const fetch_issues_csv = (filters) =>
  get_api(group_url('/needs_attention/issues/csv', issues_csv_params(filters)), true).then(
    csv_download
  );

/*
 * Additional payments by CSV upload, plus the grant-gated product /recalc. Bodies and query params
 * come from additional_payments/upload/requests.js; every month is 'YYYY-MM-01'.
 */
const upload_url = (path, params = {}) => additional_url(`/upload${path}`, params);

// Server-generated text/csv header row; returned as text, not an envelope.
export const fetch_upload_template = () =>
  get_api(upload_url('/template'), true).then((res) => res.data);

// Parses, stores the file in S3 and persists the preview; the 120 s formdata ceiling applies.
export const validate_upload = (file, pay_month) =>
  post_api(
    upload_url('/validate'),
    to_form_data(validate_fields(file, pay_month)),
    true,
    'formdata'
  ).then(envelope_data);

export const fetch_upload_run = (run_uuid) =>
  get_api(upload_url('/run', run_params(run_uuid)), true).then(envelope_data);

export const fetch_upload_runs = (pay_month, limit, offset) =>
  get_api(upload_url('/runs', runs_params(pay_month, limit, offset)), true).then(envelope_data);

export const fetch_upload_file_link = (run_uuid) =>
  get_api(upload_url('/file', file_params(run_uuid)), true).then(envelope_data);

// One transaction for up to 1,000 rows plus a 20 s recompute budget: wider than the 30 s default.
export const apply_upload = (payload) =>
  post_api(upload_url('/apply'), payload, true, 'none', MONTH_BATCH_TIMEOUT_MS).then(envelope_data);

export const discard_upload = (payload) =>
  post_api(upload_url('/discard'), payload, true).then(envelope_data);

// The grant-gated recompute: post-apply remainder "Recalculate now" both use it.
export const post_recalc = (payload) =>
  post_api(group_url('/recalc'), payload, true).then(envelope_data);

/*
 * month close/reopen runs. A create answers in seconds and the server closes the teachers one by
 * one, so these keep the default timeout.
 */
export const post_month_run = (payload) =>
  post_api(group_url('/month/runs'), payload, true).then(envelope_data);

export const fetch_month_run = (run_uuid, bucket, limit, offset) =>
  get_api(group_url('/month/run', month_run_params(run_uuid, bucket, limit, offset)), true).then(
    envelope_data
  );

export const fetch_month_runs = (pay_month, limit, offset) =>
  get_api(group_url('/month/runs', month_runs_params(pay_month, limit, offset)), true).then(
    envelope_data
  );

export const post_month_run_cancel = (payload) =>
  post_api(group_url('/month/run/cancel'), payload, true).then(envelope_data);

export const post_month_run_resume = (payload) =>
  post_api(group_url('/month/run/resume'), payload, true).then(envelope_data);

/*
 * close versions (reads only). `state` is close_versions/params.js view state and `mode` the
 * effective mode; every month is 'YYYY-MM'.
 */
const close_versions_url = (path, params) => group_url(`/close_versions${path}`, params);

export const fetch_close_versions_summary = (pay_month) =>
  get_api(close_versions_url('/summary', close_versions_summary_params(pay_month)), true).then(
    envelope_data
  );

export const fetch_close_versions_list = (pay_month, state, mode) =>
  get_api(
    close_versions_url('/list', close_versions_list_params(pay_month, state, mode)),
    true
  ).then(envelope_data);

export const fetch_close_versions_teacher_month = (pay_month, teacher_uuid) =>
  get_api(
    close_versions_url('/teacher_month', teacher_month_params(pay_month, teacher_uuid)),
    true
  ).then(envelope_data);

export const fetch_close_versions_csv = (pay_month, state, mode) =>
  get_api(close_versions_url('/csv', close_versions_csv_params(pay_month, state, mode)), true).then(
    csv_download
  );

/* Email logs (statement preview + editable text). No send endpoint yet. */
const email_url = (path, params) => group_url(`/email${path}`, params);

export const fetch_email_preview = (pay_month, teacher_uuid) =>
  get_api(email_url('/preview', preview_params(pay_month, teacher_uuid)), true).then(envelope_data);

export const fetch_email_text = (pay_month) =>
  get_api(email_url('/text', text_params(pay_month)), true).then(envelope_data);

export const save_email_text = (payload) =>
  post_api(email_url('/text'), payload, true).then(envelope_data);

/*
 * base rate history (append-only). The history GET also says whether this user may add a change
 * (can_change); a POST refusal carries reason (sentence) + error_code.
 */
export const fetch_base_rate_history = (teacher_uuid) =>
  get_api(group_url('/base_rate/history', { teacher_uuid }), true).then(envelope_data);

export const post_base_rate = (payload) =>
  post_api(group_url('/base_rate'), payload, true).then(envelope_data);

/*
 * the teacher app's Earnings switch. Bulk: overview/earnings_logic.js bulk_payload; the answer has
 * per-result counts and one line per teacher (staff refused by name).
 */
export const post_earnings_visible = (payload) =>
  post_api(group_url('/teacher_attrs/earnings_visible'), payload, true).then(envelope_data);

// Raw response: payment_attributes.jsx checks the envelope itself so a malformed one shows its error state.
export const fetch_teacher_attrs = (teacher_uuid) =>
  get_api(group_url('/teacher_attrs', { teacher_uuid }), true);

/*
 * payment details review (TEACHER_OPS_PAYMENT or TEACHER_PAYMENT_SUPER_USER only; anyone else gets
 * 403 here even with the module grant). Bodies come from payment_details/requests.js.
 */
const payout_details_url = (path, params) => group_url(`/payout_details${path}`, params);

export const fetch_payout_details_pending = (after_id) =>
  get_api(payout_details_url('/pending', pending_params(after_id)), true).then(envelope_data);

export const fetch_payout_detail = (detail_uuid) =>
  get_api(payout_details_url('/view', view_params(detail_uuid)), true).then(envelope_data);

export const fetch_payout_ifsc = (ifsc) =>
  get_api(payout_details_url('/ifsc', ifsc_params(ifsc)), true).then(envelope_data);

export const post_payout_detail_reveal = (payload) =>
  post_api(payout_details_url('/reveal'), payload, true).then(envelope_data);

export const post_payout_detail_approve = (payload) =>
  post_api(payout_details_url('/approve'), payload, true).then(envelope_data);

export const post_payout_detail_reject = (payload) =>
  post_api(payout_details_url('/reject'), payload, true).then(envelope_data);

export const fetch_payout_details_awaiting = (since_month, after_teacher_id) =>
  get_api(
    payout_details_url('/awaiting', awaiting_params(since_month, after_teacher_id)),
    true
  ).then(envelope_data);

/*
 * payment email history and the "Send payment emails to teachers" switch (payment grant; the POST
 * is TEACHER_PAYMENT_SUPER_USER only). Params/bodies come from payment_emails/requests.js.
 */
export const fetch_email_outbox = (filters, before_id) =>
  get_api(email_url('/outbox', outbox_params(filters, before_id)), true).then(envelope_data);

export const fetch_email_switch = () => get_api(email_url('/switch'), true).then(envelope_data);

export const post_email_switch = (payload) =>
  post_api(email_url('/switch'), payload, true).then(envelope_data);

/*
 * Ops invoices (both payments roles; a module user without one gets 403 here). The PDF is a Blob:
 * a refusal's envelope is read back into JSON so the tab routes it like any other.
 */
const blob_download = (url) =>
  get_api(url, true, null, 'blob').then(
    (res) => ({ blob: res.data, file_name: disposition_file_name(res && res.headers) }),
    (e) => with_json_error(e).then((err) => Promise.reject(err))
  );

export const fetch_invoices_list = (pay_month, status, after_id) =>
  get_api(group_url('/invoices/list', invoice_list_params(pay_month, status, after_id)), true).then(
    envelope_data
  );

export const fetch_invoice_pdf = (invoice_uuid) =>
  blob_download(group_url('/invoices/pdf', pdf_params(invoice_uuid)));

/* bank payouts (both payments roles). Params and bodies come from payouts/requests.js. */
const payouts_url = (path, params) => group_url(`/payouts${path}`, params);

export const fetch_payout_eligibility = (pay_month) =>
  get_api(payouts_url('/eligibility', eligibility_params(pay_month)), true).then(envelope_data);

export const post_payout_generate = (payload) =>
  post_api(payouts_url('/files/generate'), payload, true, 'none', MONTH_BATCH_TIMEOUT_MS).then(
    envelope_data
  );

export const fetch_payout_files = (pay_month) =>
  get_api(payouts_url('/files', files_params(pay_month)), true).then(envelope_data);

export const fetch_payout_file_csv = (file_uuid) =>
  blob_download(payouts_url('/file/csv', file_csv_params(file_uuid)));

export const upload_payout_response = (pay_month, client_request_id, file) =>
  post_api(
    payouts_url('/responses/upload'),
    to_form_data(upload_fields(pay_month, client_request_id, file)),
    true,
    'formdata'
  ).then(envelope_data);

export const fetch_payout_response = (response_uuid) =>
  get_api(payouts_url('/responses/preview', response_preview_params(response_uuid)), true).then(
    envelope_data
  );

export const post_payout_response_apply = (response_uuid) =>
  post_api(
    payouts_url('/responses/apply'),
    apply_body(response_uuid),
    true,
    'none',
    MONTH_BATCH_TIMEOUT_MS
  ).then(envelope_data);

export const fetch_payout_held = (after_id) =>
  get_api(payouts_url('/held', held_params(after_id)), true).then(envelope_data);

export const post_payout_held_resolve = (payload) =>
  post_api(payouts_url('/held/resolve'), payload, true).then(envelope_data);

export const post_payout_retry = (payload) =>
  post_api(payouts_url('/failed/retry'), payload, true).then(envelope_data);

export const post_payout_paid_outside = (payload) =>
  post_api(payouts_url('/paid_outside'), payload, true).then(envelope_data);

export const fetch_payout_teacher_month = (pay_month, teacher_uuid) =>
  get_api(
    payouts_url('/teacher_month', payout_teacher_month_params(pay_month, teacher_uuid)),
    true
  ).then(envelope_data);

export const fetch_payout_deleted_accounts = (after_teacher_id) =>
  get_api(payouts_url('/deleted_accounts', deleted_params(after_teacher_id)), true).then(
    envelope_data
  );

// the banner/badge count, and one teacher's deleted state for the profile banner.
export const fetch_payout_deleted_summary = () =>
  get_api(payouts_url('/deleted_accounts/summary'), true).then(envelope_data);

export const fetch_payout_deleted_teacher = (teacher_uuid) =>
  get_api(
    payouts_url('/deleted_accounts/teacher', deleted_teacher_params(teacher_uuid)),
    true
  ).then(envelope_data);

export const fetch_payout_claims = (status, after_id) =>
  get_api(payouts_url('/claims', claims_params(status, after_id)), true).then(envelope_data);

export const post_payout_claim_resolve = (payload) =>
  post_api(payouts_url('/claims/resolve'), payload, true).then(envelope_data);

export const fetch_payout_holidays = (year) =>
  get_api(payouts_url('/holidays', holidays_params(year)), true).then(envelope_data);

export const post_payout_holiday = (payload) =>
  post_api(payouts_url('/holidays'), payload, true).then(envelope_data);

export const post_payout_holiday_remove = (payload) =>
  post_api(payouts_url('/holidays/remove'), payload, true).then(envelope_data);
